import React, { useCallback, useEffect, useState } from "react";
import { TopBar } from "@/components/TopBar";
import { PaymentStatus } from "@/components/PaymentStatus";
import { GiftShare } from "@/components/GiftShare";
import { useI18n } from "@/i18n";
import { useRouter } from "@/router/Router";
import { fetchOrder } from "@/lib/api";
import type { Order } from "@/types";
import { formatUzs } from "@/lib/format";
import { useBackButton } from "@/lib/hooks";
import { Ornament, OrderJourney, ReceiptLabel } from "@/components/Premium";

export function OrderConfirmationPage({ orderId }: { orderId: string }) {
  const { t, locale } = useI18n();
  const { navigate } = useRouter();
  const [order, setOrder] = useState<Order | null>(null);

  const load = useCallback(() => {
    fetchOrder(orderId).then((o) => setOrder(o ?? null)).catch(() => {});
  }, [orderId]);

  useEffect(() => load(), [load]);
  useBackButton(() => navigate({ name: "home" }));

  return (
    <>
      <TopBar title={t("orderConfirmation.title")} onBack={() => navigate({ name: "home" })} showCart={false} />
      <main className="page page--no-nav" style={{ paddingTop: "var(--space-5)" }}>
        <div style={{ textAlign: "center" }}>
          <div className="sheet__success-icon" style={{ width: 64, height: 64, fontSize: 28 }}>
            ✓
          </div>
          <h1 style={{ fontFamily: "var(--font-display)", fontSize: 21, fontWeight: 400, margin: "0 0 6px" }}>{t("orderConfirmation.title")}</h1>
          <p style={{ color: "var(--color-ink-soft)", marginBottom: "var(--space-5)" }}>{t("orderConfirmation.number", { id: order?.number ?? "…" })}</p>
        </div>

        {order && <PaymentStatus order={order} onSettled={load} />}
        {order?.isGift && <GiftShare order={order} />}

        {order && (
          <>
            <ReceiptLabel order={order} />
            <p style={{ fontSize: 13.5, color: "var(--color-ink-soft)", margin: "16px 2px 0" }}>
              {order.deliveryMethod === "pickup" ? t("orderConfirmation.pickupSummary", { address: t("checkout.pickupAddress") }) : t("orderConfirmation.deliverySummary")}
            </p>
            <p style={{ fontSize: 12.5, color: "var(--color-muted)", margin: "8px 2px 0" }}>{t("orderConfirmation.cancelWindow")}</p>
            <Ornament title={t("journey.title")} />
            <OrderJourney order={order} />
          </>
        )}

        <button className="btn btn--primary" style={{ marginBottom: 10 }} onClick={() => navigate({ name: "orderDetail", orderId })}>
          {t("orderConfirmation.viewOrder")}
        </button>
        <button className="btn btn--ghost" style={{ width: "100%" }} onClick={() => navigate({ name: "catalog" })}>
          {t("orderConfirmation.continueShopping")}
        </button>
      </main>
    </>
  );
}
